import { FC, Fragment, useMemo } from 'react';
import { convertLatexToMarkup } from 'mathlive';
import 'mathlive/static.css';
import 'mathlive/fonts.css';
import './MathText.scss';

// Formulas are stored inline in question/option text as \( latex \).
// Text without delimiters is treated as plain text, so existing quizzes render unchanged.
export const MATH_START = '\\(';
export const MATH_END = '\\)';
const MATH_SEGMENT_REGEX = /\\\(([\s\S]*?)\\\)/g;

// Commands that can emit links, ids, classes or data attributes into the rendered HTML
const UNSAFE_LATEX_COMMANDS = /\\(href|url|htmlData|htmlStyle|cssId|class)\b/g;

export type MathSegment = { type: 'text' | 'math'; value: string; start: number; end: number };

// MathLive writes boxes that were never filled (fraction parts, matrix cells, exponents...) as \placeholder{}
const EMPTY_SLOT_REGEX = /\\placeholder\b/;
// A box that was filled and then cleared is written as an empty group instead: \frac{}{4}, x^{}, \sqrt{}
const EMPTY_GROUP_REGEX = /\{\s*\}/;
// ...and a cleared matrix cell as nothing between the & and \\ separators
const MATRIX_REGEX = /\\begin\{([a-zA-Z]*matrix\*?)\}([\s\S]*?)\\end\{\1\}/g;

const hasEmptyMatrixCell = (latex: string): boolean => {
    MATRIX_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = MATRIX_REGEX.exec(latex)) !== null) {
        const rows = match[2].split('\\\\');
        if (rows.some((row) => row.split('&').some((cell) => !cell.trim()))) return true;
    }
    return false;
};

export const isIncompleteLatex = (latex: string): boolean =>
    !latex.trim() || EMPTY_SLOT_REGEX.test(latex) || EMPTY_GROUP_REGEX.test(latex) || hasEmptyMatrixCell(latex);

export const hasIncompleteFormula = (text?: string | null): boolean =>
    !!text && parseMathText(text).some((segment) => segment.type === 'math' && isIncompleteLatex(segment.value));

export const hasMath =(text?: string | null): boolean => !!text && new RegExp(MATH_SEGMENT_REGEX.source).test(text);

export const parseMathText = (text: string): MathSegment[] => {
    const segments: MathSegment[] = [];
    let lastIndex = 0;
    for (const match of Array.from(text.matchAll(MATH_SEGMENT_REGEX))) {
        const start = match.index ?? 0;
        const end = start + match[0].length;
        if (start > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, start), start: lastIndex, end: start });
        }
        segments.push({ type: 'math', value: match[1], start, end });
        lastIndex = end;
    }
    if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex), start: lastIndex, end: text.length });
    }
    return segments;
};

export const latexToMarkup = (latex: string): string => {
    try {
        return convertLatexToMarkup(latex.replace(UNSAFE_LATEX_COMMANDS, ''));
    } catch {
        return '';
    }
};

interface MathTextProps {
    value?: string | null;
    className?: string;
}

export const MathText: FC<MathTextProps> = ({ value, className }) => {
    const segments = useMemo(() => parseMathText(value ?? ''), [value]);

    return (
        <span className={className}>
            {segments.map((segment, index) => (
                segment.type === 'math'
                    ? <span key={index} className="mathFormula" dangerouslySetInnerHTML={{ __html: latexToMarkup(segment.value) }} />
                    : <Fragment key={index}>{segment.value}</Fragment>
            ))}
        </span>
    );
};
